import { useMemo, useState } from 'react';
import { PIECES } from '../../data';
import { CORPUS, PIANO_ENTRIES, PRELUDE_ENTRIES, SONATA_ENTRIES, VARIATION_ENTRIES } from '../../data/chorales/corpusIndex';
import { keyName, parseKey } from '../../music/key';
import type { TensionPoint } from '../../music/tension';
import { buildView } from '../model';
import { Sparkline } from '../components/Sparkline';
import { PlayIcon } from '../components/Icons';
import { go } from '../hooks';
import { requestPlay } from '../intent';

interface Card {
  id: string;
  title: string;
  catalogue: string;
  collection: string;
  homeKey: string;
  tune: string;
  count: string;
  group: string;
  /** Set order within a group that is not ordered by catalogue number (variation sets: Mozart, then Beethoven). */
  rank?: number;
  points: TensionPoint[];
  length: number;
  search: string;
}

const WTC = 'The Well-Tempered Clavier, Book I';
const PIANO = 'Later piano music';
const ORDER = [WTC, 'Keyboard', PIANO, 'Theme and variations', 'Mozart piano sonatas', 'St Matthew Passion', 'St John Passion', 'Christmas Oratorio', 'Motets', 'From the cantatas', 'Four-part chorales'];
const FIRST_SHOWN = 12;

function groupOf(collection: string, keyboard: boolean): string {
  if (collection.includes('Well-Tempered')) return WTC;
  if (keyboard) return 'Keyboard';
  if (/Op\. \d/.test(collection)) return PIANO;
  if (collection === 'Theme and variations') return 'Theme and variations';
  if (collection === 'Piano sonata') return 'Mozart piano sonatas';
  for (const g of ['St Matthew Passion', 'St John Passion', 'Christmas Oratorio']) if (collection.includes(g)) return g;
  if (collection.includes('Motet')) return 'Motets';
  if (collection.includes('Cantata')) return 'From the cantatas';
  return 'Four-part chorales';
}

/** Catalogue order: BWV 244.15 before 244.44, BWV 36.8 before 248.5. */
function byCatalogue(a: Card, b: Card): number {
  if (a.rank !== undefined && b.rank !== undefined) return a.rank - b.rank;
  const x = (a.catalogue.match(/\d+/g) ?? ['0']).map(Number);
  const y = (b.catalogue.match(/\d+/g) ?? ['0']).map(Number);
  for (let i = 0; i < Math.max(x.length, y.length); i++) if ((x[i] ?? 0) !== (y[i] ?? 0)) return (x[i] ?? 0) - (y[i] ?? 0);
  return a.id.localeCompare(b.id);
}

const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export function Library() {
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState<'all' | 'major' | 'minor'>('all');
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const cards = useMemo<Card[]>(() => {
    const keyboard: Card[] = Object.values(PIECES).map((piece) => {
      const v = buildView(piece);
      return {
        id: piece.id,
        title: piece.title,
        catalogue: piece.catalogue,
        collection: piece.collection,
        homeKey: piece.homeKey,
        tune: '',
        count: `${piece.moments.length} moments`,
        group: groupOf(piece.collection, true),
        points: v.tension.points,
        length: v.length,
        search: '',
      };
    });
    const chorales: Card[] = CORPUS.entries.map((e, i) => ({
      id: e.id,
      title: e.title,
      catalogue: e.composer ? `${e.composer} · ${e.catalogue}` : e.catalogue,
      ...(e.kind === 'variations' ? { rank: i } : {}),
      collection: e.collection,
      homeKey: e.homeKey,
      tune: e.tune,
      count:
        e.kind === 'sonata'
          ? `${e.sections?.length ?? 0} sections`
          : e.kind === 'variations'
            ? `${(e.sections ?? []).filter((s) => /^variation/.test(s[2])).length} variations`
            : `${e.phrases.length} phrases`,
      group: groupOf(e.collection, false),
      points: e.spark.map((v, i) => ({ t: ((i + 0.5) / e.spark.length) * e.length, v: v / 100, event: i })),
      length: e.length,
      search: '',
    }));
    return [...keyboard, ...chorales]
      .map((c) => ({ ...c, search: fold(`${c.title} ${c.catalogue} ${c.catalogue.replace('BWV ', '')} ${c.tune} ${c.collection} ${keyName(parseKey(c.homeKey))}`) }))
      .sort(byCatalogue);
  }, []);

  const q = fold(query.trim());
  const visible = cards.filter((c) => (!q || q.split(/\s+/).every((w) => c.search.includes(w))) && (mode === 'all' || parseKey(c.homeKey).mode === mode));
  const searching = q.length > 0 || mode !== 'all';

  return (
    <div>
      <header className="page-head">
        <span className="label">Pieces</span>
        <h1>Pieces in the atlas</h1>
        <p>
          {[
            PRELUDE_ENTRIES.length ? `${PRELUDE_ENTRIES.length + 1} preludes of the Well-Tempered Clavier` : 'Bach’s Prelude in C',
            `${CORPUS.entries.filter((e) => !e.kind).length} Bach chorales`,
            SONATA_ENTRIES.length ? `${SONATA_ENTRIES.length} Mozart sonata movements` : '',
            VARIATION_ENTRIES.length ? `${VARIATION_ENTRIES.length} sets of variations by Mozart and Beethoven` : '',
            PIANO_ENTRIES.length ? `${PIANO_ENTRIES.map((e) => `${e.composer}’s ${e.title}`).join(', ')}` : '',
          ]
            .filter(Boolean)
            .join(', ')
            .replace(/, ([^,]*)$/, ' and $1')}
          . Each line on a card is the piece’s tension from first note to last. Open one to listen.
        </p>
      </header>
      <div className="library-tools">
        <input
          type="search"
          className="search"
          placeholder="Search by title, BWV, K., tune or key"
          aria-label="Search pieces"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="seg" role="group" aria-label="Mode">
          {(['all', 'major', 'minor'] as const).map((m) => (
            <button key={m} aria-pressed={mode === m} onClick={() => setMode(m)}>
              {m === 'all' ? 'All keys' : m === 'major' ? 'Major' : 'Minor'}
            </button>
          ))}
        </div>
        <span className="muted" aria-live="polite">
          {visible.length} of {cards.length}
        </span>
        <span className="grow" />
        <button
          className="btn"
          disabled={!visible.length}
          onClick={() => {
            const c = visible[Math.floor(Math.random() * visible.length)];
            requestPlay(c.id);
            go(c.id);
          }}
          title="Open a piece at random and start playing it"
        >
          <PlayIcon /> Surprise me
        </button>
      </div>

      {ORDER.map((g) => {
        const items = visible.filter((c) => c.group === g);
        if (!items.length) return null;
        const expanded = searching || open[g] || items.length <= FIRST_SHOWN + 2;
        const shown = expanded ? items : items.slice(0, FIRST_SHOWN);
        return (
          <section key={g} className="family">
            <span className="label">
              {g} · {items.length}
              {g === WTC && PRELUDE_ENTRIES.length > 0 && (
                <>
                  {' '}
                  · <a href="#keys">all 24 keys on one page</a>
                </>
              )}
              {g === PIANO && <> · imported from MIDI, harmony read by rule</>}
              {g === 'Theme and variations' && (
                <>
                  {' '}
                  · <a href="#variations">every variation under its theme</a>
                </>
              )}
              {g === 'Mozart piano sonatas' && CORPUS.forms.length > 0 && (
                <>
                  {' '}
                  · <a href="#forms">comparisons: recapitulations, refrains, variations</a>
                </>
              )}
            </span>
            <div className="path-grid">
              {shown.map((c) => (
                <a key={c.id} className="path-card" href={`#${c.id}`}>
                  <h3 style={{ fontSize: '1.05rem' }}>{c.title}</h3>
                  <span className="muted" style={{ fontSize: '0.8rem' }}>
                    {c.catalogue} · {keyName(parseKey(c.homeKey))} · {c.count}
                  </span>
                  <span className="glyph">
                    <Sparkline points={c.points} length={c.length} width={200} height={36} label={`Tension of ${c.title}`} />
                  </span>
                </a>
              ))}
            </div>
            {!expanded && (
              <button className="btn quiet" style={{ marginLeft: -12, marginTop: 6 }} onClick={() => setOpen((o) => ({ ...o, [g]: true }))}>
                Show all {items.length}
              </button>
            )}
          </section>
        );
      })}
      {!visible.length && <p className="muted">Nothing matches. Try a BWV number (“244”), a word from the title, or a key (“D minor”).</p>}
      <p className="foot">
        Chorale scores: music21 corpus. Harmonic analyses: When in Rome (mostly by Dmitri Tymoczko, with Andrew Jones and others; proofread by Hamish Robb and Luis Valencia; CC BY-SA 4.0). Mozart:
        scores and phrase endings from the DCML Mozart sonatas (CC BY-NC-SA 4.0, non-commercial), harmony and form from When in Rome. Variations: scores, phrases and
        harmony from TAVERN (Devaney and others, CC BY-SA 4.0). Pieces whose score and analysis
        could not be lined up were left out automatically; see the Method page.
      </p>
    </div>
  );
}
